import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Briefcase, TrendingUp, Building2, GraduationCap } from "lucide-react";
import DecorativeDivider from "@/components/DecorativeDivider";

const highlights = [
  { icon: GraduationCap, title: "B.E. in Computer Engineering", desc: "Agnel Institute of Technology & Design, Goa" },
  { icon: Briefcase, title: "Senior Software Engineer", desc: "At Allegion, a reputed multinational company" },
  { icon: Building2, title: "Based in Bengaluru", desc: "Hybrid work model, once a week in office" },
  { icon: TrendingUp, title: "Strong Career Stability", desc: "Consistent growth and professional development" },
];

export default function ProfessionalBackground() {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.15 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      ref={ref}
      className="py-12 md:py-16 px-4 sm:px-6 md:px-12"
      style={{ background: "#F7F4EF" }}
      data-testid="professional-section"
    >
      <div className="max-w-4xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={visible ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7 }}
        >
          <p
            className="text-xs sm:text-sm tracking-[0.2em] uppercase mb-2 font-semibold"
            style={{ color: "#8A9A5B", fontFamily: "'Manrope', sans-serif" }}
          >
            Career
          </p>
          <h2
            className="text-3xl md:text-4xl font-normal tracking-tight mb-6 md:mb-8"
            style={{ fontFamily: "'Playfair Display', serif", color: "#2D2D2A" }}
          >
            Professional Background
          </h2>
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-5 mb-8">
          {highlights.map((item, i) => {
            const Icon = item.icon;
            return (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 16 }}
                animate={visible ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.5, delay: 0.15 + i * 0.1 }}
                className="p-4 sm:p-5 rounded-xl transition-all duration-300 hover:shadow-xs"
                style={{ background: "#FDFBF7", border: "1px solid #E6E2DD" }}
              >
                <Icon className="w-5 h-5 mb-2.5" style={{ color: "#8A9A5B" }} strokeWidth={1.5} />
                <h3
                  className="text-sm sm:text-base font-medium mb-1"
                  style={{ fontFamily: "'Manrope', sans-serif", color: "#2D2D2A" }}
                >
                  {item.title}
                </h3>
                <p
                  className="text-xs sm:text-sm font-light leading-relaxed"
                  style={{ fontFamily: "'Manrope', sans-serif", color: "#999999" }}
                >
                  {item.desc}
                </p>
              </motion.div>
            );
          })}
        </div>

        <motion.p
          initial={{ opacity: 0 }}
          animate={visible ? { opacity: 1 } : {}}
          transition={{ duration: 0.7, delay: 0.5 }}
          className="text-sm sm:text-base md:text-lg leading-relaxed font-light max-w-2xl"
          style={{ color: "#2D2D2A", fontFamily: "'Manrope', sans-serif", opacity: 0.85 }}
        >
          I am passionate about technology and continuous self-improvement. I believe in balancing ambition with family values and emotional maturity.
        </motion.p>

        <div className="mt-8 sm:mt-10">
          <DecorativeDivider />
        </div>
      </div>
    </section>
  );
}
